import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Shield, Loader2, AlertTriangle, Clock, CheckCircle2, Ban, Activity as ActivityIcon,
} from "lucide-react";
import api from "@/lib/api";

const KINDS = [
  { key: "sensitive", label: "Sensitive actions", icon: ActivityIcon },
  { key: "approvals", label: "Approvals", icon: CheckCircle2 },
  { key: "denied", label: "Denied attempts", icon: Ban },
];

const STATUS_STYLE = {
  PENDING: "bg-amber-50 text-amber-700 border-amber-500",
  APPROVED: "bg-emerald-50 text-emerald-700 border-emerald-500",
  REJECTED: "bg-slate-100 text-slate-600 border-slate-300",
  FAILED: "bg-rose-50 text-rose-700 border-rose-500",
  SUCCESS: "bg-emerald-50 text-emerald-700 border-emerald-500",
  ERROR: "bg-rose-50 text-rose-700 border-rose-500",
};

export default function ComplianceDashboard() {
  const { t } = useTranslation();
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [kind, setKind] = useState("sensitive");
  const [hours, setHours] = useState(24);
  const [loading, setLoading] = useState(true);

  const loadSummary = async () => {
    try {
      const { data } = await api.get("/compliance/summary");
      setSummary(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const loadActivity = async (k = kind, h = hours) => {
    setLoading(true);
    try {
      const { data } = await api.get("/compliance/activity", { params: { kind: k, hours: h, limit: 200 } });
      setItems(data.items || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setLoading(false); }
  };

  useEffect(() => { loadSummary(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { loadActivity(kind, hours); /* eslint-disable-next-line */ }, [kind, hours]);

  return (
    <div className="space-y-6" data-testid="compliance-page">
      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Compliance</div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          {t("compliance.title", "Compliance dashboard")}
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">{t("compliance.subtitle", "Real-time overview of collaborator activity, approvals and denied attempts.")}</p>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi icon={Clock} label="Pending approvals" value={summary?.pending_approvals ?? "—"} tone="amber" testid="kpi-pending" />
        <Kpi icon={ActivityIcon} label="Sensitive actions (24h)" value={summary?.sensitive_actions_24h ?? "—"} tone="violet" testid="kpi-sensitive" />
        <Kpi icon={Ban} label="Denied attempts (24h)" value={summary?.denied_attempts_24h ?? "—"} tone="rose" testid="kpi-denied" />
        <Kpi icon={Shield} label="Active collaborators" value={`${summary?.collaborators_active ?? "—"} / ${summary?.collaborators_total ?? "—"}`} tone="emerald" testid="kpi-collabs" />
      </div>

      {/* Suspicious signals */}
      {summary && (summary.suspicious_signals.excessive_denies.length + summary.suspicious_signals.excessive_rejections.length > 0) && (
        <div className="rounded-md border border-rose-200 bg-rose-50 p-4" data-testid="suspicious-panel">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-rose-700" />
            <div className="text-sm font-bold text-rose-800">{t("compliance.suspicious", "Suspicious collaborator activity")}</div>
          </div>
          <div className="space-y-1 text-sm text-rose-800">
            {summary.suspicious_signals.excessive_denies.map((s) => (
              <div key={"d-" + s.actor_id}>
                <span className="font-semibold">{s.username}</span> — {s.denies_24h} permission denies in the last 24h
              </div>
            ))}
            {summary.suspicious_signals.excessive_rejections.map((s) => (
              <div key={"r-" + s.actor_id}>
                <span className="font-semibold">{s.username}</span> — {s.rejections_24h} approval requests rejected in the last 24h
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        {KINDS.map((k) => {
          const Icon = k.icon;
          const on = kind === k.key;
          return (
            <button key={k.key} data-testid={`compl-kind-${k.key}`} onClick={() => setKind(k.key)} className={"inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border text-xs font-semibold " + (on ? "bg-[#0F62FE] text-white border-[#0F62FE]" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50")}>
              <Icon className="w-3.5 h-3.5" /> {k.label}
            </button>
          );
        })}
        <div className="ml-auto flex items-center gap-2">
          <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Window</label>
          <select data-testid="compl-hours" value={hours} onChange={(e) => setHours(Number(e.target.value))} className="h-9 rounded-lg border border-slate-300 bg-white px-2 text-sm">
            <option value={1}>1 hour</option>
            <option value={24}>24 hours</option>
            <option value={168}>7 days</option>
            <option value={720}>30 days</option>
          </select>
        </div>
      </div>

      {/* Activity table */}
      <div className="bg-white border border-slate-200 rounded-md">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] font-bold uppercase tracking-widest text-slate-500 bg-slate-50">
                <th className="px-4 py-2">Date/time</th>
                <th className="px-4 py-2">Collaborator</th>
                <th className="px-4 py-2">Action</th>
                <th className="px-4 py-2">Target</th>
                <th className="px-4 py-2 text-right">Amount</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">IP</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> {t("common.loading")}</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-500">{t("compliance.empty", "No activity for the selected window.")}</td></tr>
              ) : items.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`compl-row-${r.id}`}>
                  <td className="px-4 py-2 text-slate-700 whitespace-nowrap">{r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</td>
                  <td className="px-4 py-2 font-semibold text-slate-900">{r.actor_username || "—"}</td>
                  <td className="px-4 py-2 text-slate-800">{r.action}</td>
                  <td className="px-4 py-2 text-slate-700">{r.target || "—"}</td>
                  <td className="px-4 py-2 text-right font-mono text-slate-700">{r.amount != null ? `$${Number(r.amount).toFixed(2)}` : "—"}</td>
                  <td className="px-4 py-2"><span className={"text-xs font-semibold px-2 py-0.5 rounded-full border " + (STATUS_STYLE[r.status] || "border-slate-300 text-slate-700")}>{r.status || "—"}</span></td>
                  <td className="px-4 py-2 text-slate-500 font-mono text-xs">{r.ip || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, tone, testid }) {
  const cls = {
    amber: "text-amber-700 bg-amber-50",
    violet: "text-violet-700 bg-violet-50",
    rose: "text-rose-700 bg-rose-50",
    emerald: "text-emerald-700 bg-emerald-50",
  }[tone] || "text-slate-700 bg-slate-50";
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4" data-testid={testid}>
      <div className={"inline-flex items-center justify-center w-9 h-9 rounded-lg mb-2 " + cls}><Icon className="w-4 h-4" /></div>
      <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className="text-2xl font-bold text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{value}</div>
    </div>
  );
}
